import { KnowledgeTracking } from "@/lib/tracking/knowledge";
import { useState, useCallback, ChangeEvent, DragEvent, useEffect } from "react";
import { Button, toast, Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import { Input } from "@altimateai/lego";
import { Label } from "@altimateai/lego";
import { Textarea } from "@altimateai/lego";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@altimateai/lego";
import { PrivacySelector } from "@/components/form/PrivacySelector";
import { LoadingButton, Spinner, Stack } from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import {
  createKnowledgeSource,
  getKnowledgeSourceById,
  updateKnowledgeSource,
} from "@/helpers/apis";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MdxMarkdownEditor } from "@/components/markdownEditor";
import { KnowledgeCatalogItem } from "./types";
import { useTenantInfo } from "@/helpers/hooks";
import { Privacy } from "@/helpers/constants";
import { PrivacyLevel } from "@/types";
import { useBreadcrumb } from "@/components/breadcrumbs";

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ACCEPTED_FILE_EXTENSIONS = ".pdf, .doc, .docx";
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const knowledgeSourceSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().min(1, "Purpose is required"),
    content: z.string().optional(),
    privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC]).default(Privacy.PRIVATE),
    file: z
      .instanceof(File)
      .refine(file => file.size <= MAX_FILE_SIZE_BYTES, `Max file size is ${MAX_FILE_SIZE_MB}MB.`)
      .refine(
        file => ACCEPTED_FILE_TYPES.includes(file.type),
        `Only ${ACCEPTED_FILE_EXTENSIONS} files are accepted.`
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.content && !data.file) {
      ctx.addIssue({
        path: ["content"],
        code: z.ZodIssueCode.custom,
        message: "Either paste content or upload a file.",
      });
      ctx.addIssue({
        path: ["file"],
        code: z.ZodIssueCode.custom,
        message: "Either paste content or upload a file.",
      });
    }
  });

type KnowledgeSourceFormValues = z.infer<typeof knowledgeSourceSchema>;

export default function CreateUpdateKnowledgeSourceV1() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("paste-content");
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { enableAdvancedGuardRails } = useTenantInfo();

  const {
    data: knowledgeSource,
    isLoading: isKnowledgeSourceLoading,
    error: loadError,
  } = useQuery({
    queryKey: ["knowledgeSource", id],
    queryFn: () => getKnowledgeSourceById(id!),
    enabled: !!id,
  });

  useBreadcrumb(knowledgeSource ? "Edit Knowledge" : "Create Knowledge", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      { label: "Knowledge Hub", pathname: "/knowledge-hub" },
    ],
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<KnowledgeSourceFormValues>({
    resolver: zodResolver(knowledgeSourceSchema),
    defaultValues: {
      name: "",
      description: "",
      content: "",
      privacy: "private",
      file: undefined,
    },
  });

  useEffect(() => {
    if (id && knowledgeSource) {
      reset({
        name: knowledgeSource.name,
        description: knowledgeSource.description,
        content: knowledgeSource.content?.pages?.[0]?.content || "",
        privacy: knowledgeSource.privacy || Privacy.PRIVATE,
      });
    }
  }, [id, knowledgeSource, reset]);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (data: Partial<KnowledgeCatalogItem>) => {
      if (id) {
        return updateKnowledgeSource(id, data);
      }
      return createKnowledgeSource(data);
    },
    onSuccess: data => {
      if (id) {
        KnowledgeTracking.sourceEdited(id);
      } else {
        KnowledgeTracking.sourceCreated(activeTab, watch("privacy") || "private");
      }
      toast({
        title: "Knowledge created successfully",
      });
      navigate(`/knowledge-hub/${data?.id}`);
      queryClient.invalidateQueries({ queryKey: ["knowledgeSource", id] });
      reset({ name: "", description: "", content: "", privacy: "private", file: undefined });
      setSelectedFileName(null);
    },
    onError: (error: Error) => {
      if (id) {
        KnowledgeTracking.sourceEditFailed(id, error.message);
      } else {
        KnowledgeTracking.sourceCreateFailed(error.message);
      }
      toast({
        title: "Error creating knowledge",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleTabChange = (newTabValue: string) => {
    setActiveTab(newTabValue);
    if (newTabValue === "upload-file") {
      KnowledgeTracking.uploadTabSelected(id || null);
    }
  };

  const handleFileSelect = useCallback(
    (file: File | null) => {
      if (file) {
        setValue("file", file, { shouldValidate: true });
        setSelectedFileName(file.name);
      } else {
        setValue("file", undefined, { shouldValidate: true });
        setSelectedFileName(null);
      }
    },
    [setValue]
  );

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const onSubmit = async (data: KnowledgeSourceFormValues) => {
    await mutate({ ...data, content: { pages: [{ content: data.content || "" }] } });
  };

  const clearFile = () => {
    handleFileSelect(null);
  };

  // Add a keyboard handler for accessibility on the dropzone
  const onDropzoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      document.getElementById("file-upload-input")?.click();
    }
  };

  if (isKnowledgeSourceLoading) {
    return <Spinner />;
  }

  return (
    <div>
      {loadError && (
        <Alert variant="destructive" className="al-my-4">
          <AlertTitle>Failed to load knowledge source</AlertTitle>
          <AlertDescription>
            {loadError instanceof Error ? loadError.message : String(loadError)}
          </AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" className="al-gap-4 al-py-4">
          <Stack direction="column" className="al-gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Project Alpha Documentation"
              {...register("name")}
            />
            {errors.name && <p className="al-text-red-500 al-text-sm">{errors.name.message}</p>}
          </Stack>
          <Stack direction="column" className="al-gap-2">
            <Label htmlFor="description">Purpose</Label>
            <Textarea
              id="description"
              placeholder="Describe the purpose of this knowledge..."
              {...register("description")}
              className="al-min-h-[80px]"
            />
            {errors.description && (
              <p className="al-text-red-500 al-text-sm">{errors.description.message}</p>
            )}
          </Stack>

          {/* Privacy Section - Only show if advanced guardrails are enabled */}
          {enableAdvancedGuardRails && (
            <Stack direction="column" className="al-gap-2">
              <PrivacySelector
                value={watch("privacy") || Privacy.PRIVATE}
                onValueChange={(value: PrivacyLevel) => setValue("privacy", value)}
                entityType="knowledge source"
              />
              {errors.privacy && (
                <p className="al-text-red-500 al-text-sm">{errors.privacy.message}</p>
              )}
            </Stack>
          )}

          <Tabs value={activeTab} onValueChange={handleTabChange} className="al-w-full">
            <TabsList className="al-grid al-w-full al-grid-cols-2">
              <TabsTrigger value="paste-content">Paste Content</TabsTrigger>
              <TabsTrigger value="upload-file">Upload File</TabsTrigger>
            </TabsList>
            <TabsContent value="paste-content">
              <Stack direction="column" className="al-gap-2 al-mt-2">
                <MdxMarkdownEditor
                  markdown={watch("content") || ""}
                  onChange={value => {
                    setValue("content", value);
                  }}
                  className="al-bg-white al-rounded-md al-border al-border-gray-200"
                  contentEditableClassName="al-min-h-[350px]"
                  originalMarkdown={knowledgeSource?.content?.pages?.[0]?.content}
                />
                {errors.content && (
                  <p className="al-text-red-500 al-text-sm">{errors.content.message}</p>
                )}
              </Stack>
            </TabsContent>
            <TabsContent value="upload-file">
              <Stack direction="column" className="al-gap-2 al-mt-2">
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onClick={() => document.getElementById("file-upload-input")?.click()}
                  onKeyDown={onDropzoneKeyDown}
                  role="button"
                  tabIndex={0}
                  className={`al-p-4 al-border-2 al-border-dashed al-rounded-md al-text-center al-cursor-pointer
                                ${isDragging ? "al-border-blue-500" : "al-border-gray-300"}
                                hover:al-border-gray-400`}
                >
                  <input
                    type="file"
                    id="file-upload-input"
                    className="al-hidden"
                    onChange={onFileChange}
                    accept={ACCEPTED_FILE_EXTENSIONS}
                    disabled
                  />
                  {selectedFileName ? (
                    <Stack direction="column" align="center" className="al-gap-1">
                      <p className="al-text-sm al-text-gray-700">
                        Selected file: {selectedFileName}
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          clearFile();
                        }}
                        className="al-text-red-500"
                      >
                        Remove file
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="column" justify="center" className="al-min-h-[200px]">
                      <p className="al-text-sm al-text-gray-500 ">Coming soon...</p>
                    </Stack>
                  )}
                </div>
                {errors.file && <p className="al-text-red-500 al-text-sm">{errors.file.message}</p>}
              </Stack>
            </TabsContent>
          </Tabs>
        </Stack>
        <Stack
          direction="row"
          justify="end"
          className="al-px-4 al-gap-2 al-sticky al-bottom-[-32px] al-bg-white al-py-4 al-z-10 al-border-t"
        >
          <Button type="button" variant="outline" asChild>
            <Link to={`/knowledge-hub/${id || ""}`}>Cancel</Link>
          </Button>
          <LoadingButton type="submit" loading={isLoading}>
            {id ? "Update" : "Create"} Knowledge
          </LoadingButton>
        </Stack>
      </form>
    </div>
  );
}
